import { useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useTenant } from "@/contexts/TenantContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import {
  MODULE_SECTIONS, MANAGED_ROLES, LEVELS, LEVEL_LABEL, DEFAULT_HIDDEN,
} from "@/admin/modules";

const ROLE_LABEL = { admin: "Admin", super_admin: "Super Admin" };

const LEVEL_TONE = {
  hidden: "text-slate-500",
  view: "text-blue-600 dark:text-blue-300",
  edit: "text-amber-600 dark:text-amber-300",
  full: "text-emerald-600 dark:text-emerald-300",
};

function defaultLevel(key) {
  return DEFAULT_HIDDEN.has(key) ? "hidden" : "full";
}

export default function AccessControl() {
  const { tenant } = useTenant();
  const { refresh: refreshPerms } = usePermissions();
  const [matrix, setMatrix] = useState(null); // { admin: {key:lvl}, super_admin: {...} }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  function hydrate(stored) {
    const next = {};
    for (const role of MANAGED_ROLES) {
      next[role] = {};
      for (const sec of MODULE_SECTIONS) {
        for (const m of sec.items) {
          const ov = stored?.[role]?.[m.key];
          next[role][m.key] = LEVELS.includes(ov) ? ov : defaultLevel(m.key);
        }
      }
    }
    return next;
  }

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/access-control");
      setMatrix(hydrate(r.data?.matrix || {}));
    } catch (e) {
      toast.error(formatErr(e));
      setMatrix(hydrate({}));
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tenant?.id]);

  function setCell(role, key, level) {
    setMatrix((m) => ({ ...m, [role]: { ...m[role], [key]: level } }));
  }

  function setRoleBulk(role, level) {
    setMatrix((m) => {
      const next = { ...m[role] };
      for (const sec of MODULE_SECTIONS) for (const it of sec.items) next[it.key] = level;
      return { ...m, [role]: next };
    });
  }

  function resetDefaults() {
    setMatrix(hydrate({}));
    toast.info("Reset to defaults — remember to save.");
  }

  async function save() {
    setSaving(true);
    try {
      await api.put("/admin/access-control", { matrix });
      toast.success("Access control saved");
      refreshPerms?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  }

  const moduleCount = useMemo(
    () => MODULE_SECTIONS.reduce((n, s) => n + s.items.length, 0),
    [],
  );

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="access-control-title">
            <ShieldCheck className="w-6 h-6 text-primary" /> Access Control
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Control what <b>Admin</b> and <b>Super Admin</b> roles can do in each module — hide it,
            allow view-only, allow edit, or allow full (incl. delete). Configured per tenant.
          </p>
          {tenant?.name && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Configuring tenant:</span>
              <Badge variant="outline" className="font-semibold" data-testid="access-control-tenant">{tenant.name}</Badge>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={resetDefaults} className="gap-1.5" data-testid="access-control-reset">
            <RotateCcw className="w-4 h-4" /> Reset defaults
          </Button>
          <Button onClick={save} disabled={saving || loading} className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="access-control-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
          </Button>
        </div>
      </div>

      {loading || !matrix ? (
        <div className="py-20 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold min-w-[240px]">Module ({moduleCount})</th>
                  {MANAGED_ROLES.map((role) => (
                    <th key={role} className="px-4 py-3 font-semibold w-44">
                      <div className="flex flex-col gap-1.5">
                        <span>{ROLE_LABEL[role]}</span>
                        <Select onValueChange={(v) => setRoleBulk(role, v)}>
                          <SelectTrigger className="h-7 text-[11px]" data-testid={`bulk-${role}`}>
                            <SelectValue placeholder="Set all…" />
                          </SelectTrigger>
                          <SelectContent>
                            {LEVELS.map((l) => <SelectItem key={l} value={l}>{LEVEL_LABEL[l]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODULE_SECTIONS.map((sec) => (
                  <>
                    <tr key={`sec-${sec.label}`} className="bg-muted/25 border-t border-border">
                      <td colSpan={1 + MANAGED_ROLES.length} className="px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground/80">
                        {sec.label}
                      </td>
                    </tr>
                    {sec.items.map((m) => (
                      <tr key={m.key} className="border-t border-border hover:bg-muted/20" data-testid={`row-module-${m.key}`}>
                        <td className="px-4 py-2 font-medium">{m.label}</td>
                        {MANAGED_ROLES.map((role) => {
                          const lvl = matrix[role]?.[m.key] || "hidden";
                          return (
                            <td key={role} className="px-4 py-2">
                              <Select value={lvl} onValueChange={(v) => setCell(role, m.key, v)}>
                                <SelectTrigger className={`h-8 text-xs font-medium ${LEVEL_TONE[lvl]}`} data-testid={`perm-${role}-${m.key}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {LEVELS.map((l) => (
                                    <SelectItem key={l} value={l} className={LEVEL_TONE[l]}>{LEVEL_LABEL[l]}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
